import { FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IonButtons,
  IonContent,
  IonItem,
  IonLabel,
  IonTitle,
  useIonToast,
  useIonViewDidEnter,
} from '@ionic/react'

import { BackButton } from '@components/atoms/BackButton'
import { Title } from '@components/atoms/Title'
import { Loading } from '@/components/atoms/Loading'
import { TimelineError } from '@/components/organisms/TimelineError'
import { Main } from '@/components/templates/main'
import { Header } from './components/Header'
import { StakingVaultHistory } from './components/StakingVaultHistory'

import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { useVirtualList } from '@/lib/hooks/useVirtualList'
import { useWallet } from '@/lib/hooks/wallet'
import { Routes } from '@/router/routes'

import { TransactionsType } from '@/enums'

export const StakeVault: FC = () => {
  const { t } = useTranslation('common')
  const { data: authUser } = useUserInfo()
  const [presentToast] = useIonToast()

  const {
    refetchFirstTransactionsPage,
    transactions,
    hasGetTransactionsNextPage,
    fetchTransactionsNextPage,
    isGetTransactionsLoading,
    isGetTransactionsFetching,
    hasTransactionsError,
    wallet,
    refetchRPKWallet,
  } = useWallet({
    userInfo: authUser,
    transactionsType: TransactionsType.Stake,
  })

  const refetchStake = useCallback(async () => {
    await Promise.all([refetchRPKWallet(), refetchFirstTransactionsPage()])
  }, [refetchRPKWallet, refetchFirstTransactionsPage])

  useIonViewDidEnter(() => {
    refetchRPKWallet()
  }, [])

  useEffect(() => {
    if (transactions.length && hasTransactionsError) {
      presentToast({
        message: t('timeline.errors.stakeVault'),
        position: 'top',
        duration: 3000,
      })
    }
  }, [hasTransactionsError, transactions, presentToast, t])

  const { isAtTop, atTopStateChange, onEndReached } = useVirtualList({
    hasNextPage: hasGetTransactionsNextPage,
    fetchNextPage: fetchTransactionsNextPage,
  })

  const header = (
    <>
      <IonButtons slot="start">
        <BackButton defaultHref={`${Routes.VAULT}?rpk=true`} />
      </IonButtons>
      <IonTitle>
        <Title className="text-center my-0" level={3}>
          {t('wallet.stakeVault.stakeVault')}
        </Title>
      </IonTitle>
    </>
  )

  const renderContent = () => {
    if (hasTransactionsError && !transactions.length) {
      return <TimelineError translation={'timeline.errors.stakeVault'} />
    }

    if (
      isGetTransactionsLoading ||
      (isGetTransactionsFetching && !transactions.length)
    )
      return <Loading />

    if (!transactions?.length) {
      return (
        <IonItem className="mb-5">
          <IonLabel className="ion-text-wrap">
            <div className="flex flex-col justify-center items-center">
              <div>
                <img
                  src="/coin.png"
                  srcSet="/coin@2x.png 2x"
                  className="py-6"
                />
              </div>
              <div className="text-white text-[1rem] mb-2 w-[12rem] font-bold text-center">
                {t('wallet.stakeVault.noStakes')}
              </div>
            </div>
          </IonLabel>
        </IonItem>
      )
    }

    return (
      <>
        <Header stakedBalance={wallet?.stakedBalance} />
        <IonContent>
          <StakingVaultHistory
            transactions={transactions}
            atTopStateChange={atTopStateChange}
            onEndReached={onEndReached}
          />
        </IonContent>
      </>
    )
  }

  return (
    <Main refetch={refetchStake} isRefetchActive={isAtTop} header={header}>
      {renderContent()}
    </Main>
  )
}
